import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, CircularProgress, TextField, IconButton, Grid
} from '@mui/material';
import { Home, Print, AccountBalanceWallet } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const todayStr = () => new Date().toISOString().split('T')[0];

const BUCKET_COLOR = { Current: '#2e7d32', '1-30': '#1565c0', '31-60': '#f9a825', '61-90': '#f57c00', '90+': '#c62828' };

const PayablesAging = () => {
  const navigate = useNavigate();
  const [asOf,    setAsOf]    = useState(todayStr());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/payables/aging`, { headers: hdrs(), params: { asOf } });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to compute payables aging');
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buckets = data?.buckets || ['Current', '1-30', '31-60', '61-90', '90+'];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } }`}</style>

      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
            <Box display="flex" alignItems="center">
              <AccountBalanceWallet sx={{ mr: 1, color: '#c62828' }} />
              <Typography variant="h5" sx={{ color: '#c62828' }}>Payables Aging</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => window.print()} title="Print" disabled={!data}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Print />
              </IconButton>
              <IconButton onClick={() => navigate('/payables')} title="Payables Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Outstanding vendor balances grouped by how far past the due date they are.
            "Current" is not yet due.
          </Typography>

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2} className="no-print">
            <TextField label="As of date" type="date" size="small" value={asOf}
              onChange={e => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }} />
            <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#c6282815' }}>
              {loading ? <CircularProgress size={20} /> : <AccountBalanceWallet />}
            </IconButton>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {data && (
            <>
              <Grid container spacing={1.5} mb={2}>
                {buckets.map(b => (
                  <Grid item xs={6} sm={2.4} key={b}>
                    <Card elevation={2} sx={{ borderLeft: `4px solid ${BUCKET_COLOR[b]}` }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={800} color={BUCKET_COLOR[b]} lineHeight={1}>
                          {sym()} {fmt(data.totals[b])}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {b === 'Current' ? 'Not yet due' : `${b} days`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {data.rows.length === 0 ? (
                <Alert severity="success">No outstanding vendor balances. All settled.</Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Supplier</strong></TableCell>
                        {buckets.map(b => (
                          <TableCell key={b} align="right" sx={{ color: BUCKET_COLOR[b] }}><strong>{b}</strong></TableCell>
                        ))}
                        <TableCell align="right"><strong>Total Owed</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.rows.map(r => (
                        <TableRow key={r.supplierCode} hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/suppliers`)}>
                          <TableCell><strong>{r.supplierCode}</strong> — {r.supplierName}</TableCell>
                          {buckets.map(b => (
                            <TableCell key={b} align="right"
                              sx={{ color: r[b] > 0 ? BUCKET_COLOR[b] : 'text.disabled' }}>
                              {r[b] > 0 ? fmt(r[b]) : '—'}
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(r.outstanding)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Totals</TableCell>
                        {buckets.map(b => (
                          <TableCell key={b} align="right" sx={{ fontWeight: 800, color: BUCKET_COLOR[b] }}>
                            {fmt(data.totals[b])}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 800 }}>{sym()} {fmt(data.totals.outstanding)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PayablesAging;
